import { useCallback, useMemo, useState } from 'react'
import { Combobox } from '@/components/ui/combobox'
import { useAllUsers } from '@/hooks/useUsers'
import { useUserSearch } from '@/hooks/useUserSearch'

interface OwnerFilterComboboxProps {
  value: string | null
  onValueChange: (value: string | null) => void
  placeholder?: string
  className?: string
}

export function OwnerFilterCombobox({
  value,
  onValueChange,
  placeholder = 'All Owners',
  className = 'w-[180px]',
}: OwnerFilterComboboxProps) {
  const [search, setSearch] = useState('')
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const { data: allUsers, isLoading: allUsersLoading } = useAllUsers()
  const isSearching = search.trim().length >= 2
  const { data: searchData, isFetching: searchFetching } = useUserSearch({
    query: search,
  })

  const options = useMemo(() => {
    const source = isSearching
      ? (searchData?.pages.flatMap(p => p.users) ?? [])
      : (allUsers ?? [])
    const opts = source.map(u => ({
      value: u.id,
      label: u.display_name || u.email || u.id,
    }))
    if (value && selectedLabel && !opts.some(o => o.value === value)) {
      opts.unshift({ value, label: selectedLabel })
    }
    return opts
  }, [isSearching, searchData, allUsers, value, selectedLabel])

  const handleChange = useCallback(
    (next: string | null) => {
      if (next) {
        const match = options.find(o => o.value === next)
        if (match) setSelectedLabel(match.label)
      } else {
        setSelectedLabel(null)
      }
      onValueChange(next)
    },
    [options, onValueChange]
  )

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={handleChange}
      placeholder={placeholder}
      isLoading={isSearching ? searchFetching : allUsersLoading}
      className={className}
      onSearchChange={setSearch}
      filterOptions={false}
    />
  )
}
